import {
    ADMIN_ROLES,
    ANALYST_ROLES,
    CUSTODIAN_ROLES,
    LENDER_ADMIN_ROLES,
    LENDER_ROLES,
    READONLY_ROLES,
    RoleEnv
} from '../constants/iam-roles.constants';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

/**
 * Describes a single API endpoint and the exact IAG roles that may call it.
 * Placeholders in `pathTemplate` ({typeId}, {id}) are replaced at runtime.
 */
export interface ApiEndpointDef {
  /** Unique stable identifier — used as track key in templates */
  id: string;
  method: HttpMethod;
  /** URL template — {typeId} and {id} are dynamic path parameters */
  pathTemplate: string;
  description: string;
  /** Exact IAG role strings from iam-roles.constants — no aliases, no mapping */
  allowedRoles: readonly string[];
  /**
   * Which deployment environment this endpoint is intended for.
   * 'BOTH' — accessible in every environment (roles still validated per-env by EnvRoleService).
   * 'NONPROD' — only meaningful on lower-env deployments.
   * 'PROD'    — only meaningful on production deployments.
   */
  env: RoleEnv | 'BOTH';
}

/**
 * Config-driven catalog of all FRM Configuration API endpoints.
 *
 * To add a new endpoint: append a new entry here.
 * No changes are needed in any component or service.
 *
 * `allowedRoles` intentionally lists both NONPROD and PROD role constants so
 * the same catalog works in every environment.  EnvRoleService.hasRole() filters
 * down to only the roles valid for the CURRENT deployment at runtime.
 */
export const CONFIGURATION_API_CATALOG: ApiEndpointDef[] = [
  {
    id: 'get-lenders',
    method: 'GET',
    pathTemplate: '/api/lenders',
    description: 'Retrieve all lenders',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES, ...READONLY_ROLES],
    env: 'BOTH'
  },
  {
    id: 'get-configuration-categories',
    method: 'GET',
    pathTemplate: '/api/setup/configuration-categories',
    description: 'Retrieve all configuration categories',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES, ...READONLY_ROLES],
    env: 'BOTH'
  },
  {
    id: 'get-configuration-types',
    method: 'GET',
    pathTemplate: '/api/setup/configuration-types',
    description: 'Retrieve all configuration types',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES, ...READONLY_ROLES],
    env: 'BOTH'
  },
  {
    id: 'get-configuration-values',
    method: 'GET',
    pathTemplate: '/api/setup/configuration-types/{typeId}/values',
    description: 'Retrieve all values for a configuration type',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES, ...READONLY_ROLES],
    env: 'BOTH'
  },
  {
    id: 'create-configuration-value',
    method: 'POST',
    pathTemplate: '/api/setup/configuration-types/{typeId}/values',
    description: 'Add a new value to a configuration type',
    allowedRoles: [...ADMIN_ROLES],
    env: 'BOTH'
  },
  {
    id: 'update-configuration-value',
    method: 'PUT',
    pathTemplate: '/api/setup/configuration-types/{typeId}/values/{id}',
    description: 'Update an existing configuration value',
    allowedRoles: [...ADMIN_ROLES],
    env: 'BOTH'
  },
  {
    id: 'reorder-configuration-values',
    method: 'PUT',
    pathTemplate: '/api/setup/configuration-types/{typeId}/values/reorder',
    description: 'Reorder configuration values by display order',
    allowedRoles: [...ADMIN_ROLES],
    env: 'BOTH'
  },
  {
    id: 'get-bank-submission-lenders',
    method: 'GET',
    pathTemplate: '/api/lenders/financial-submissions/lenders',
    description: 'List lenders as {lenderId, lenderName} tuples for the bank submission dropdown (internal users only). Returns numeric lenderId so the frontend can construct lender-first URLs.',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'get-bank-submissions',
    method: 'GET',
    pathTemplate: '/api/lenders/{lenderId}/financial-submissions',
    description: 'List bank financial submissions for landing page (lender-first, uses numeric lenderId)',
    allowedRoles: [
      ...ADMIN_ROLES,
      ...ANALYST_ROLES,
      ...READONLY_ROLES,
      ...LENDER_ROLES,
      ...LENDER_ADMIN_ROLES,
      ...CUSTODIAN_ROLES,
    ],
    env: 'BOTH'
  },
  {
    id: 'get-bank-submission-access-context',
    method: 'GET',
    pathTemplate: '/api/lenders/financial-submissions/context',
    description: 'Resolves authenticated user access mode and lender scope context for financial submissions',
    allowedRoles: [
      ...ADMIN_ROLES,
      ...ANALYST_ROLES,
      ...READONLY_ROLES,
      ...LENDER_ROLES,
      ...LENDER_ADMIN_ROLES,
      ...CUSTODIAN_ROLES,
    ],
    env: 'BOTH'
  },
  {
    id: 'get-lender-attestations',
    method: 'GET',
    pathTemplate: '/api/lenders/{lenderId}/attestations',
    description: 'List lender attestation history rows used by Financial Submissions > Attestation Review',
    allowedRoles: [
      ...ADMIN_ROLES,
      ...ANALYST_ROLES,
      ...READONLY_ROLES,
      ...LENDER_ROLES,
      ...LENDER_ADMIN_ROLES,
      ...CUSTODIAN_ROLES,
    ],
    env: 'BOTH'
  },
  {
    id: 'get-lender-attestation-detail',
    method: 'GET',
    pathTemplate: '/api/lenders/{lenderId}/financial-submissions/{reportingPeriod}/attestation',
    description: 'Get lender attestation detail for a specific reporting period',
    allowedRoles: [
      ...ADMIN_ROLES,
      ...ANALYST_ROLES,
      ...READONLY_ROLES,
      ...LENDER_ROLES,
      ...LENDER_ADMIN_ROLES,
      ...CUSTODIAN_ROLES,
    ],
    env: 'BOTH'
  },
  {
    id: 'get-bank-submission-detail-by-context',
    method: 'GET',
    pathTemplate: '/api/lenders/{lenderId}/financial-submissions/{reportingPeriod}',
    description: 'Get bank financial submission detail by lender and financial period',
    allowedRoles: [
      ...ADMIN_ROLES,
      ...ANALYST_ROLES,
      ...READONLY_ROLES,
      ...LENDER_ROLES,
      ...LENDER_ADMIN_ROLES,
      ...CUSTODIAN_ROLES,
    ],
    env: 'BOTH'
  },
  {
    id: 'save-bank-submission',
    method: 'POST',
    pathTemplate: '/api/lenders/{lenderId}/financial-submissions/{reportingPeriod}/save',
    description: 'Save bank financial submission edits without final transition',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES, ...LENDER_ROLES, ...LENDER_ADMIN_ROLES],
    env: 'BOTH'
  },
  {
    id: 'submit-bank-submission-for-review',
    method: 'POST',
    pathTemplate: '/api/lenders/{lenderId}/financial-submissions/{reportingPeriod}/submit-for-review',
    description: 'Submit bank financial submission for lender to internal review transition',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES, ...LENDER_ROLES, ...LENDER_ADMIN_ROLES],
    env: 'BOTH'
  },
  {
    id: 'submit-bank-submission-final',
    method: 'POST',
    pathTemplate: '/api/lenders/{lenderId}/financial-submissions/{reportingPeriod}/submit-final',
    description: 'Submit bank financial submission for final analyst/internal transition',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES, ...LENDER_ROLES, ...LENDER_ADMIN_ROLES],
    env: 'BOTH'
  },
  {
    id: 'submit-bank-submission-return-to-lender',
    method: 'POST',
    pathTemplate: '/api/lenders/{lenderId}/financial-submissions/{reportingPeriod}/return-to-lender',
    description: 'Submit bank financial submission back to lender for a review cycle',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'override-bank-submission',
    method: 'POST',
    pathTemplate: '/api/lenders/{lenderId}/financial-submissions/{reportingPeriod}/overrides',
    description: 'Persist lender or analyst override updates for a bank submission line item',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES, ...LENDER_ROLES, ...LENDER_ADMIN_ROLES],
    env: 'BOTH'
  },
  {
    id: 'approve-bank-submission-line',
    method: 'PUT',
    pathTemplate: '/api/lenders/{lenderId}/financial-submissions/{reportingPeriod}/approvals/{lineValueId}',
    description: 'Persist a line-level approval state for a bank submission line item',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'clear-bank-submission-line-approval',
    method: 'DELETE',
    pathTemplate: '/api/lenders/{lenderId}/financial-submissions/{reportingPeriod}/approvals/{lineValueId}',
    description: 'Clear a persisted line-level approval state for a bank submission line item',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'unlock-bank-submission',
    method: 'POST',
    pathTemplate: '/api/lenders/{lenderId}/financial-submissions/{reportingPeriod}/unlock',
    description: 'Unlock a completed bank submission for another review cycle',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES, ...LENDER_ROLES, ...LENDER_ADMIN_ROLES],
    env: 'BOTH'
  },
  {
    id: 'patch-bank-submission-fiscal-quarter',
    method: 'PATCH',
    pathTemplate: '/api/lenders/{lenderId}/financial-submissions/{reportingPeriod}/fiscal-quarter',
    description: 'Persist Financial Quarter Submitted, apply workflow transition, and recalculate Fiscal YTD',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES, ...LENDER_ROLES, ...LENDER_ADMIN_ROLES],
    env: 'BOTH'
  },
  {
    id: 'resolve-sll-worksheet',
    method: 'POST',
    pathTemplate: '/api/sll/worksheets/resolve',
    description: 'Find or create an SLL Calculation worksheet from CLS entry context (lenderId, financialPeriod, frameworkType)',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'get-sll-worksheet',
    method: 'GET',
    pathTemplate: '/api/sll/worksheets/{id}',
    description: 'Get SLL Calculation worksheet detail by worksheet id',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'calculate-sll-bank-worksheet',
    method: 'POST',
    pathTemplate: '/api/sll/worksheets/{id}/bank/calculate',
    description: 'Compute and persist the Bank SLL calculation (Calculated/Adjusted/Final SLL, MF LenderIQ adjustment, historical comparisons)',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'get-sll-bank-worksheet',
    method: 'GET',
    pathTemplate: '/api/sll/worksheets/{id}/bank',
    description: 'Get the last persisted Bank SLL calculation for a worksheet',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'calculate-sll-non-bank-worksheet',
    method: 'POST',
    pathTemplate: '/api/sll/worksheets/{id}/non-bank/calculate',
    description: 'Compute and persist the Non-Bank SLL calculation (Adjusted ANW, Loan Amount Limit, historical averaging, Specialty Minimum, MF LenderIQ adjustment, final sequence, materiality)',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'get-sll-non-bank-worksheet',
    method: 'GET',
    pathTemplate: '/api/sll/worksheets/{id}/non-bank',
    description: 'Get the last persisted Non-Bank SLL calculation for a worksheet',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'preview-sll-worksheet',
    method: 'POST',
    pathTemplate: '/api/sll/worksheets/{id}/preview',
    description: 'Compute a live, non-authoritative calculation preview using the caller\'s current draft inputs (Step 5, worksheet inputs no longer autosave)',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'save-sll-worksheet',
    method: 'POST',
    pathTemplate: '/api/sll/worksheets/{id}/save',
    description: 'Authoritative Save \u2014 validates required inputs, recalculates, and persists Final SLL (Story 1.6)',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'submit-sll-to-reviewer',
    method: 'POST',
    pathTemplate: '/api/sll/worksheets/{id}/submit-to-reviewer',
    description: 'Submit an In Progress SLL worksheet to Reviewer',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'return-sll-to-preparer',
    method: 'POST',
    pathTemplate: '/api/sll/worksheets/{id}/return-to-preparer',
    description: 'Return an Approval Request SLL worksheet to Preparer (no reason required)',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'approve-sll-worksheet',
    method: 'POST',
    pathTemplate: '/api/sll/worksheets/{id}/approve',
    description: 'Approve \u2014 locks the worksheet, blocked when Recalculation Required (Story 1.6)',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'recalculate-sll-worksheet',
    method: 'POST',
    pathTemplate: '/api/sll/worksheets/{id}/recalculate',
    description: 'Recalculate \u2014 visible/callable only when Recalculation Required; non-persisting preview using refreshed source/dependency values (Story 1.9, FR24)',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'list-appl-comments',
    method: 'GET',
    pathTemplate: '/api/comments',
    description: 'List generic shared comments for a subjectType/subjectId (Story 1.7, design.md \u00a714, new appl_cmnt_dtl comment stack)',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'create-appl-comment',
    method: 'POST',
    pathTemplate: '/api/comments',
    description: 'Create a generic shared comment; effective role is resolved server-side (Story 1.7, FR64)',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'update-appl-comment',
    method: 'PUT',
    pathTemplate: '/api/comments/{commentId}',
    description: 'Update an owned generic shared comment; owner-only edit is enforced server-side (Story 1.7, FR52)',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'delete-appl-comment',
    method: 'DELETE',
    pathTemplate: '/api/comments/{commentId}',
    description: 'Delete an owned generic shared comment; owner-only delete is enforced server-side',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'get-screen-lock-status',
    method: 'GET',
    pathTemplate: '/api/screen-locks/status',
    description: 'Retrieve current lock state for all configured screens for a lender/financial period',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'get-screen-lock-status-by-screen',
    method: 'GET',
    pathTemplate: '/api/screen-locks/status/{screenCode}',
    description: 'Retrieve current lock state for a single screen; a missing lock record is reported as unlocked',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'get-screen-lock-periods',
    method: 'GET',
    pathTemplate: '/api/screen-locks/periods',
    description: 'Retrieve existing financial periods with screen lock records for a lender',
    allowedRoles: [...ADMIN_ROLES, ...ANALYST_ROLES],
    env: 'BOTH'
  },
  {
    id: 'change-screen-lock',
    method: 'PUT',
    pathTemplate: '/api/screen-locks/{screenCode}',
    description: 'Lock or unlock a single screen for a lender/financial period (Internal Admin only)',
    allowedRoles: [...ADMIN_ROLES],
    env: 'BOTH'
  },
  {
    id: 'unlock-reset-screen-lock',
    method: 'PUT',
    pathTemplate: '/api/screen-locks/{screenCode}/unlock-reset',
    description: 'Unlock a single screen and reset its workflow state for a lender/financial period (Internal Admin only)',
    allowedRoles: [...ADMIN_ROLES],
    env: 'BOTH'
  }
];

export function resolveApiEndpointPath(
  endpointId: string,
  params?: Record<string, string | number>
): string {
  const endpoint = CONFIGURATION_API_CATALOG.find((entry) => entry.id === endpointId);
  if (!endpoint) {
    throw new Error(`Unknown API endpoint id: ${endpointId}`);
  }

  if (!params) {
    return endpoint.pathTemplate;
  }

  return Object.entries(params).reduce((path, [key, value]) => {
    return path.replace(`{${key}}`, encodeURIComponent(String(value)));
  }, endpoint.pathTemplate);
}
